import React, { useEffect } from "react";
import {
  Backdrop,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  ThemeProvider,
} from "@material-ui/core";
// Local
import { iOS } from "../device";
import { CloseIcon } from "../components";
import { useInstalledPWA } from "../lib";
import { useMobile, usePreownedMode, preownedTheme } from "../themes";
import { Sidebar } from "./Sidebar";
import { PreownedSidebar } from "./PreownedSidebar";
import { useStyles } from "./MainLayout.styles";
import {
  UIActions,
  WatchActions,
  UISelectors,
  useDispatch,
  useSelector,
} from "../state";

function MainLayoutInner({ children }) {
  const isPreowned = usePreownedMode();
  const dispatch = useDispatch();
  const uiLoading = useSelector(UISelectors.loading);
  const uiNotification = useSelector(UISelectors.notification);
  const isInstalled = useInstalledPWA();
  const isMobile = useMobile();
  const classes = useStyles();
  const onClickInstallApp = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(
        UIActions.showNotification(
          "To install the app, click the Share button, scroll down and tap " +
            "Add to Home Screen",
        ),
      );
    },
    [dispatch],
  );
  const hideNotification = React.useCallback(() => {
    dispatch(UIActions.hideNotification());
  }, [dispatch]);

  useEffect(() => {
    // 52 tables of main-catalog lookups; the pre-owned admin uses none of
    // them. Keyed on mode so switching back still loads them.
    if (!isPreowned) dispatch(WatchActions.getAllLists());
  }, [dispatch, isPreowned]);

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      {isPreowned ? <PreownedSidebar /> : <Sidebar />}
      <div className={classes.mainContent}>
        <div className={classes.content}>
          {children}
          {iOS && // Example to tell iOS users to install the PWA:
            !isInstalled && (
              <div className={classes.installNotice}>
                <Button
                  variant="text"
                  color="primary"
                  size="large"
                  fullWidth={isMobile}
                  onClick={onClickInstallApp}
                >
                  Install App
                </Button>
              </div>
            )}
        </div>
      </div>
      <Backdrop className={classes.loadingBackdrop} open={uiLoading}>
        <CircularProgress size={128} />
      </Backdrop>
      <Snackbar
        style={uiNotification?.variant === "error"
          ? { zIndex: 99999, left: "55%" }
          : { zIndex: 99999 }
        }
        anchorOrigin={
          uiNotification?.variant === "error"
            ? { vertical: "top", horizontal: "center" }
            : { vertical: "bottom", horizontal: "left" }
        }
        autoHideDuration={uiNotification?.duration}
        open={!!uiNotification}
        onClose={hideNotification}
        ContentProps={{
          "aria-describedby": "notification-message",
          style: uiNotification?.variant === "error"
            ? { backgroundColor: "#c62828", color: "#fff" }
            : undefined,
        }}
        message={
          <span id="notification-message">{uiNotification?.message}</span>
        }
        action={
          <IconButton
            key="close"
            aria-label="close"
            color="inherit"
            onClick={hideNotification}
          >
            <CloseIcon />
          </IconButton>
        }
      />
    </div>
  );
}
/**
 * Wraps ONLY the pre-owned admin in its theme. Main mode renders the layout
 * untouched: a nested ThemeProvider re-orders JSS sheet injection even when
 * handed the same theme object, and index.js sets an insertion point so JSS
 * lands before assets/css/styles.css — reordering flips which rules win.
 *
 * The provider wraps a child rather than the body itself so the body's own
 * useStyles() resolves against the pre-owned theme.
 */
function _MainLayout({ children }) {
  const isPreowned = usePreownedMode();
  if (!isPreowned) return <MainLayoutInner>{children}</MainLayoutInner>;
  return (
    <ThemeProvider theme={preownedTheme}>
      <MainLayoutInner>{children}</MainLayoutInner>
    </ThemeProvider>
  );
}
export const MainLayout = React.memo(_MainLayout);
